import React, { useState, useEffect, useCallback } from 'react';
import { BookList } from './BookList';
import { BookForm } from './BookForm';
import { TagManager } from './TagManager';
import { UserManager } from './UserManager';
import WordPressImporter from './WordPressImporter';
import { Book } from '../types';
import { authApi } from '../services/api';
import './AdminPanel.css';

// Tipos para navegação
type AdminSection = 'books' | 'tags' | 'users' | 'import';

// Constantes para autenticação
const SESSION_KEY = 'admin_session';

interface SessionData {
  username: string;
  loginTime: number;
  lastActivity: number;
}

interface LoginFormProps {
  onLogin: (username: string, password: string) => Promise<boolean>;
  error: string | null;
  isLoading: boolean;
}

function LoginForm({ onLogin, error, isLoading }: LoginFormProps): React.ReactElement {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onLogin(username, password);
  };

  return (
    <div className="admin-panel__login-container">
      <div className="admin-panel__login-card">
        <div className="admin-panel__login-header">
          <h1 className="admin-panel__login-title">📚 Catálogo de Livros</h1>
          <p className="admin-panel__login-subtitle">Painel Administrativo</p>
        </div>

        <form className="admin-panel__login-form" onSubmit={handleSubmit}>
          {error && (
            <div className="admin-panel__login-error">
              {error}
            </div>
          )}

          <div className="admin-panel__login-field">
            <label htmlFor="username" className="admin-panel__login-label">
              Usuário
            </label>
            <input
              type="text"
              id="username"
              className="admin-panel__login-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Digite seu usuário"
              disabled={isLoading}
              autoComplete="username"
              required
            />
          </div>

          <div className="admin-panel__login-field">
            <label htmlFor="password" className="admin-panel__login-label">
              Senha
            </label>
            <input
              type="password"
              id="password"
              className="admin-panel__login-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Digite sua senha"
              disabled={isLoading}
              autoComplete="current-password"
              required
            />
          </div>

          <button
            type="submit"
            className="admin-panel__login-button"
            disabled={isLoading || !username.trim() || !password.trim()}
          >
            {isLoading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}


export function AdminPanel(): React.ReactElement {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [session, setSession] = useState<SessionData | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [activeSection, setActiveSection] = useState<AdminSection>('books');
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Verifica sessão ao carregar — a fonte da verdade é o SERVIDOR
  // (sessão deslizante com timeout configurável via SESSION_TIMEOUT)
  useEffect(() => {
    let cancelled = false;

    const checkSession = async (): Promise<void> => {
      const storedSession = localStorage.getItem(SESSION_KEY);
      try {
        const status = await authApi.getStatus();
        if (cancelled) return;

        if (status.isAuthenticated) {
          const base: SessionData = storedSession
            ? JSON.parse(storedSession)
            : { username: status.user?.username || '', loginTime: Date.now(), lastActivity: Date.now() };
          const sessionData: SessionData = { ...base, username: status.user?.username || base.username, lastActivity: Date.now() };
          localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
          setSession(sessionData);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem(SESSION_KEY);
        }
      } catch {
        // Servidor inacessível: mantém sessão local otimista — as chamadas
        // de API vão falhar e o interceptor de 401 cuida da volta ao login
        if (!cancelled && storedSession) {
          try {
            const sessionData: SessionData = JSON.parse(storedSession);
            setSession(sessionData);
            setIsAuthenticated(true);
          } catch {
            localStorage.removeItem(SESSION_KEY);
          }
        }
      }
    };

    void checkSession();
    return () => { cancelled = true; };
  }, []);

  const handleLogin = useCallback(async (username: string, password: string): Promise<boolean> => {
    setIsLoggingIn(true);
    setLoginError(null);

    try {
      const result = await authApi.login(username, password);
      const sessionData: SessionData = {
        username: result.user.username,
        loginTime: Date.now(),
        lastActivity: Date.now(),
      };

      localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
      setSession(sessionData);
      setIsAuthenticated(true);
      return true;
    } catch (err: any) {
      const message = err?.message || err?.response?.data?.error?.message || 'Usuário ou senha inválidos';
      setLoginError(message);
      return false;
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  const handleLogout = useCallback((): void => {
    // Encerra a sessão no servidor em segundo plano (best-effort, sem travar
    // a UI se a rede estiver ruim) antes da limpeza local
    authApi.logout().catch(() => {
      // sessão já inválida ou servidor inacessível — o cookie expira sozinho
    });
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setIsAuthenticated(false);
    setActiveSection('books');
    setEditingBook(null);
  }, []);

  const handleEditBook = useCallback((book: Book) => {
    setEditingBook(book);
    setActiveSection('books');
  }, []);

  const handleBookSubmit = useCallback((book: Book) => {
    setEditingBook(null);
    setRefreshKey((prev) => prev + 1);
    console.log('Livro salvo:', book.title);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingBook(null);
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleSectionChange = useCallback((section: AdminSection) => {
    setActiveSection(section);
    if (section !== 'books') {
      setEditingBook(null);
    }
  }, []);

  // Renderiza tela de login se não autenticado
  if (!isAuthenticated) {
    return (
      <LoginForm
        onLogin={handleLogin}
        error={loginError}
        isLoading={isLoggingIn}
      />
    );
  }

  return (
    <div className="admin-panel">
      {/* Header */}
      <header className="admin-panel__header">
        <div className="admin-panel__header-content">
          <h1 className="admin-panel__logo">📚 Catálogo de Livros</h1>
          <div className="admin-panel__user-info">
            <span className="admin-panel__username">
              Olá, {session?.username}
            </span>
            <button
              type="button"
              className="admin-panel__logout-button"
              onClick={handleLogout}
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <div className="admin-panel__container">
        {/* Sidebar Navigation */}
        <nav className="admin-panel__sidebar">
          <ul className="admin-panel__nav-list">
            <li className="admin-panel__nav-item">
              <button
                type="button"
                className={`admin-panel__nav-button ${
                  activeSection === 'books' ? 'admin-panel__nav-button--active' : ''
                }`}
                onClick={() => handleSectionChange('books')}
              >
                <span className="admin-panel__nav-icon">📚</span>
                <span className="admin-panel__nav-text">Livros</span>
              </button>
            </li>
            <li className="admin-panel__nav-item">
              <button
                type="button"
                className={`admin-panel__nav-button ${
                  activeSection === 'tags' ? 'admin-panel__nav-button--active' : ''
                }`}
                onClick={() => handleSectionChange('tags')}
              >
                <span className="admin-panel__nav-icon">🏷️</span>
                <span className="admin-panel__nav-text">Tags</span>
              </button>
            </li>
            <li className="admin-panel__nav-item">
              <button
                type="button"
                className={`admin-panel__nav-button ${
                  activeSection === 'import' ? 'admin-panel__nav-button--active' : ''
                }`}
                onClick={() => handleSectionChange('import')}
              >
                <span className="admin-panel__nav-icon">📥</span>
                <span className="admin-panel__nav-text">Importação</span>
              </button>
            </li>
            <li className="admin-panel__nav-item">
              <button
                type="button"
                className={`admin-panel__nav-button ${
                  activeSection === 'users' ? 'admin-panel__nav-button--active' : ''
                }`}
                onClick={() => handleSectionChange('users')}
              >
                <span className="admin-panel__nav-icon">👥</span>
                <span className="admin-panel__nav-text">Usuários</span>
              </button>
            </li>
          </ul>
        </nav>

        {/* Main Content */}
        <main className="admin-panel__main">
          {activeSection === 'books' && (
            <div className="admin-panel__section">
              {/* Formulário para NOVO livro - sempre visível */}
              <BookForm
                onSubmit={handleBookSubmit}
              />
              <div className="admin-panel__section-divider" />
              <BookList
                key={refreshKey}
                onEdit={handleEditBook}
                onRefresh={handleRefresh}
              />
              {/* Modal para EDITAR livro - só aparece quando editingBook existe */}
              {editingBook && (
                <BookForm
                  book={editingBook}
                  onSubmit={handleBookSubmit}
                  onCancel={handleCancelEdit}
                  isModal={true}
                />
              )}
            </div>
          )}

          {activeSection === 'tags' && (
            <div className="admin-panel__section">
              <TagManager onTagsChange={handleRefresh} />
            </div>
          )}

          {activeSection === 'import' && (
            <div className="admin-panel__section">
              <WordPressImporter onImportComplete={() => setRefreshKey(prev => prev + 1)} />
            </div>
          )}

          {activeSection === 'users' && (
            <div className="admin-panel__section">
              <UserManager onUsersChange={handleRefresh} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminPanel;
